import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Lock, Unlock, ShieldCheck, Wallet, Boxes, ArrowRight, CreditCard, Plus,
  Hash, Info, Save, CheckCircle2, XCircle, AlertTriangle,
} from "lucide-react";
import InputRupiah from "../../components/InputRupiah";
import { useAuth } from "../../context/AuthContext";
import { MonthPicker } from "../../components/ui/date-picker";
import LoadingState from "../../components/ui/loading-state";
import { bulanIniISO, formatTanggal } from "../../lib/tanggal";
import { bolehKelolaMetode } from "../../components/pembayaran/MetodePembayaran";
import { useSortState, sortRows, SortTh } from "../../components/ui/sort";

export default function FinancePengaturan() {
  const [threshold, setThreshold] = useState(5000000);
  const [periode, setPeriode] = useState(bulanIniISO());
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadStatus = async (p) => {
    try {
      const r = await api.get(`/finance/period/status/${p}`);
      setStatus(r.data);
    } catch { setStatus(null); }
  };
  useEffect(() => { loadStatus(periode); }, [periode]);

  const closePeriod = async () => {
    if (!confirm(`Kunci periode ${periode}? Semua transaksi bulan ini tidak bisa diubah kecuali via jurnal penyesuaian.`)) return;
    setBusy(true);
    try {
      await api.post("/finance/period/close", { periode });
      toast.success("Periode ditutup");
      loadStatus(periode);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-4 md:p-8 space-y-6 max-w-3xl" data-testid="finance-pengaturan">
      <div>
        <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Pengaturan</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Konfigurasi Finance</h1>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 grid place-items-center"><ShieldCheck className="w-5 h-5" /></div>
          <div>
            <p className="font-display font-bold text-base text-slate-900">Approval Threshold</p>
            <p className="text-xs text-slate-500">Transaksi ≥ nilai ini butuh persetujuan SPV</p>
          </div>
        </div>
        <InputRupiah value={threshold} onChange={n => setThreshold(n)} testid="settings-threshold" className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm" />
        <p className="text-[11px] text-slate-500 mt-2">💡 Berlaku hanya untuk role finance_staff. SPV/CEO/admin/superadmin bypass otomatis.</p>
      </div>

      <PemetaanPembayaranCard />

      <FormatNomorDokumenCard />

      <KodeNomorBuktiKasKecilCard />

      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-10 h-10 rounded-lg ${status?.locked ? "bg-rose-50 text-rose-600" : "bg-emerald-50 text-emerald-600"} grid place-items-center`}>
            {status?.locked ? <Lock className="w-5 h-5" /> : <Unlock className="w-5 h-5" />}
          </div>
          <div>
            <p className="font-display font-bold text-base text-slate-900">Period Lock (Closing Bulanan)</p>
            <p className="text-xs text-slate-500">Kunci periode agar tidak ada perubahan retroaktif</p>
          </div>
        </div>
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Periode</label>
            <MonthPicker value={periode} onChange={setPeriode} data-testid="settings-periode" />
          </div>
          <button onClick={closePeriod} disabled={busy || status?.locked} data-testid="settings-close-period" className="h-10 px-4 rounded-lg bg-rose-600 text-white hover:bg-rose-700 text-sm font-medium disabled:opacity-50">
            {status?.locked ? "Sudah Ditutup" : busy ? "Menutup..." : "Tutup Periode"}
          </button>
        </div>
        {status && (
          <p className="text-xs mt-2 text-slate-500">
            Status: <span className={`font-semibold ${status.locked ? "text-rose-700" : "text-emerald-700"}`}>{status.locked ? "🔒 Ditutup" : "🔓 Terbuka"}</span>
            {status.closed_at && ` · ditutup ${formatTanggal(status.closed_at)} oleh ${status.closed_by}`}
          </p>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold mb-3">Pengaturan Lanjutan Lainnya</p>
        {/* penanda posisi — konten tautan di bawah tidak berubah */}
        <div className="flex flex-col sm:flex-row gap-2">
          <Link
            to="/finance/budget"
            data-testid="pengaturan-link-budget"
            className="flex-1 inline-flex items-center justify-between gap-2 h-11 px-4 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-50 hover:border-emerald-300 transition"
          >
            <span className="inline-flex items-center gap-2"><Wallet className="w-4 h-4 text-emerald-700" /> Budget vs Actual</span>
            <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
          </Link>
          <Link
            to="/finance/aset-tetap"
            data-testid="pengaturan-link-aset-tetap"
            className="flex-1 inline-flex items-center justify-between gap-2 h-11 px-4 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-50 hover:border-emerald-300 transition"
          >
            <span className="inline-flex items-center gap-2"><Boxes className="w-4 h-4 text-emerald-700" /> Aset Tetap &amp; Depresiasi</span>
            <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ============ Pemetaan Pembayaran → Akun (UI untuk backend yang sudah lama siap) ============
 * Backend GET/PUT /finance/pemetaan-pembayaran menentukan akun kas/bank yang MENERIMA uang
 * penjualan per metode pembayaran (jalur sama untuk input penjualan langsung & approve nota),
 * lengkap dengan validasi "hanya kas/bank" + audit log — tapi tidak pernah punya layar.
 * Akibat nyata sebelum kartu ini ada: QRIS/GoPay/OVO/DANA/ShopeePay/kredit jatuh diam-diam ke
 * Kas Toko, dan rekening bank baru buatan owner (mis. "BCA ADAM") tidak pernah bisa dituju. */
function PemetaanPembayaranCard() {
  const { user } = useAuth();
  // Cermin `accounts.py::_boleh_atur_pemetaan` — role lama TETAP bisa, ditambah
  // izin EKSPLISIT `finance_pengaturan.update`. Sengaja bukan `can()`: `can()`
  // jatuh ke default role generik yang meloloskan semua role finance_*, jadi
  // finance_staff akan melihat dropdown yang selalu berakhir 403.
  const bolehAtur = ["finance_spv", "finance_ceo", "admin", "superadmin"].includes(user?.role)
    || bolehKelolaMetode(user);
  // Membuat metode = jalur tulis yang sama dengan modul Metode Pembayaran,
  // jadi gerbangnya harus kunci yang sama persis.
  const bolehBuatMetode = bolehKelolaMetode(user);
  const [data, setData] = useState(null);
  const [metodeApp, setMetodeApp] = useState([]);   // daftar metode dari modul payment-methods
  const [busyKey, setBusyKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [peta, pm] = await Promise.all([
        api.get("/finance/pemetaan-pembayaran"),
        api.get("/payment-methods?active_only=true").catch(() => ({ data: { items: [] } })),
      ]);
      setData(peta.data);
      setMetodeApp((pm.data.items || []).map(m => m.name));
    } catch (e) { setLoadError(e); toast.error(e?.response?.data?.detail || "Gagal memuat pemetaan pembayaran"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Hook dipanggil di sini (SEBELUM early return di bawah) supaya urutan
  // hook tetap sama tiap render — dipindah dari bawah `if (!data) return null`
  // karena React Hooks tidak boleh dipanggil kondisional (aturan hooks).
  const metodeSort = useSortState(null);

  if (loading || loadError) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="pemetaan-pembayaran-card">
        <LoadingState loading={loading} error={loadError} onRetry={load} varian="kartu" jumlahKartu={1} data-testid="loading-pemetaan-pembayaran" />
      </div>
    );
  }
  if (!data) return null;

  const akun = data.akun_kas_bank || [];
  const akunPiutangLeasing = data.akun_piutang_leasing || null;
  const efektif = data.efektif?.metode || {};
  // `peta` MENTAH kini di-key `payment_method_id` (tahan rename metode), jadi
  // tidak bisa lagi dilihat dengan nama metode. Backend mengirim bentuk
  // siap-pakai ber-key NAMA; `data.peta` lama tetap dipakai sebagai cadangan
  // untuk respons backend versi sebelumnya.
  const peta = data.peta_by_metode || data.peta || {};
  // Gabungan: metode yang pernah dipakai/baku (dari backend) + metode terdaftar di app
  // (supaya metode BARU yang belum pernah dipakai transaksi tetap bisa dipetakan duluan).
  // Urutan bawaan tetap alfabetis nama metode — daftar master kecil, TIDAK
  // dipotong query apa pun, jadi aman disortir tanpa BatasDaftarNotice.
  const metodeAllDefault = [...new Set([...(data.metode || []), ...metodeApp])].sort((a, b) => a.localeCompare(b));
  const metodeAll = sortRows(metodeAllDefault, metodeSort.sortKey, metodeSort.sortDir, {
    metode: (m) => m,
    tujuan: (m) => efektif[m]?.account_name || "",
  });

  const labelSumber = (s) => ({
    config_metode: ["diatur", "bg-emerald-50 text-emerald-700 border-emerald-200"],
    bawaan_bank: ["bawaan bank", "bg-sky-50 text-sky-700 border-sky-200"],
    config_brand_kas: ["kas cabang (diatur)", "bg-emerald-50 text-emerald-700 border-emerald-200"],
    bawaan_brand_kas: ["kas cabang", "bg-slate-50 text-slate-600 border-slate-200"],
    kas_brand_db: ["kas cabang", "bg-slate-50 text-slate-600 border-slate-200"],
    bawaan_default: ["kas default", "bg-amber-50 text-amber-700 border-amber-200"],
  }[s] || ["kas cabang / default", "bg-slate-50 text-slate-600 border-slate-200"]);

  const simpanPeta = async (metode, accountId) => {
    setBusyKey(metode);
    try {
      // Kirim `payment_method_id` kalau metodenya terdaftar — kunci kanonik
      // yang tidak putus saat metode di-rename. Nama dipakai hanya untuk nilai
      // teks lama yang tidak punya dokumen metode.
      const kunci = efektif[metode]?.payment_method_id || metode;
      const r = await api.put("/finance/pemetaan-pembayaran", { peta: { [kunci]: accountId || null } });
      toast.success(r.data.message || "Tersimpan ✓");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
    finally { setBusyKey(""); }
  };

  // Rekening bank aktif yang belum dituju metode mana pun — persis keluhan owner:
  // "aku ada buat BCA ADAM tapi di menu pembayaran malah gak ada".
  const idTertuju = new Set([
    ...Object.values(peta),
    ...Object.values(efektif).map(e => e?.account_id).filter(Boolean),
  ]);
  const bankBelumTerhubung = akun.filter(a => a.category === "bank" && !idTertuju.has(a.id));

  const buatMetodeDariAkun = async (acc) => {
    setBusyKey(acc.id);
    try {
      // Satu langkah: daftarkan metode SEKALIGUS petakan akunnya. Sebelumnya
      // ini dua panggilan ke dua modul, dan kalau yang kedua gagal, metodenya
      // sudah terlanjur ada tanpa pemetaan — uangnya jatuh ke Kas 1010.
      try {
        await api.post("/payment-methods", {
          name: acc.name, group: "Bank Transfer", is_active: true, account_id: acc.id,
        });
      } catch (e) {
        // "sudah ada" bukan masalah — petakan saja metode yang sudah ada itu
        if (!String(e?.response?.data?.detail || "").includes("sudah ada")) throw e;
        await api.put("/finance/pemetaan-pembayaran", { peta: { [acc.name]: acc.id } });
      }
      toast.success(`Metode "${acc.name}" dibuat & terpetakan ke ${acc.code} ✓`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membuat metode"); }
    finally { setBusyKey(""); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="pemetaan-pembayaran-card">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 grid place-items-center"><CreditCard className="w-5 h-5" /></div>
        <div>
          <p className="font-display font-bold text-base text-slate-900">Pemetaan Pembayaran → Akun</p>
          <p className="text-xs text-slate-500">Menentukan rekening/kas mana yang menerima uang tiap metode pembayaran (berlaku ke input penjualan & nota)</p>
        </div>
      </div>

      {bankBelumTerhubung.length > 0 && (
        <div className="mt-3 border border-amber-200 bg-amber-50 rounded-lg p-3">
          <p className="text-xs font-semibold text-amber-800 mb-2">Rekening bank yang belum dipakai metode mana pun:</p>
          <div className="space-y-1.5">
            {bankBelumTerhubung.map(a => (
              <div key={a.id} className="flex items-center justify-between gap-2">
                <span className="text-xs text-slate-700"><span className="font-mono">{a.code}</span> · {a.name}</span>
                {bolehBuatMetode && (
                  <button onClick={() => buatMetodeDariAkun(a)} disabled={busyKey === a.id}
                    data-testid={`pm-map-buat-${a.code}`}
                    className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-medium disabled:opacity-50">
                    <Plus className="w-3 h-3" /> Buat metode &amp; petakan
                  </button>
                )}
              </div>
            ))}
          </div>
          <p className="text-[10px] text-amber-700 mt-2">Sekali klik: nama rekening jadi metode pembayaran di dropdown kasir/nota + uangnya otomatis mendarat ke rekening itu.</p>
        </div>
      )}

      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-100 text-[10px] uppercase tracking-wide text-slate-500">
            <tr>
              <SortTh label="Metode" sortKeyName="metode" sortKey={metodeSort.sortKey} sortDir={metodeSort.sortDir} onSort={metodeSort.toggle} testid="pm-map-sort-metode" />
              <SortTh label="Uang mendarat di" sortKeyName="tujuan" sortKey={metodeSort.sortKey} sortDir={metodeSort.sortDir} onSort={metodeSort.toggle} testid="pm-map-sort-tujuan" />
              <th className="px-3 py-2 text-left">Atur ke akun</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {metodeAll.map(m => {
              const ef = efektif[m];
              const [lbl, cls] = labelSumber(ef?.sumber);
              return (
                <tr key={m} data-testid={`pm-map-row-${m}`}>
                  <td className="px-3 py-2 font-medium text-slate-900 whitespace-nowrap">{m}</td>
                  <td className="px-3 py-2">
                    <span className="text-xs text-slate-700 whitespace-nowrap">
                      {ef?.account_code ? <><span className="font-mono">{ef.account_code}</span> {ef.account_name}</> : "Kas Toko cabang"}
                    </span>
                    <span className={`ml-2 px-1.5 py-0.5 rounded-full border text-[10px] ${cls}`}>{lbl}</span>
                    {ef?.peringatan && (
                      <p className="text-[10px] text-amber-700 mt-1 max-w-[380px]"
                        data-testid={`pm-map-peringatan-${m}`}>
                        <AlertTriangle className="w-3 h-3 inline mb-0.5 mr-1" />{ef.peringatan}
                      </p>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <select value={peta[m] || ""} disabled={!bolehAtur || busyKey === m}
                      onChange={e => simpanPeta(m, e.target.value)}
                      data-testid={`pm-map-akun-${m}`}
                      className="h-8 px-2 rounded border border-slate-200 text-xs bg-white max-w-[240px] disabled:opacity-60">
                      <option value="">— ikuti bawaan —</option>
                      {akun.map(a => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
                      {/* C4: akun Piutang Leasing HANYA ditawarkan untuk metode
                          leasing/kartu kredit — backend menolak metode lain
                          memilihnya (pengecualian sempit, bukan "akun apa saja
                          boleh"), jadi dropdown tidak menampilkan opsi yang
                          ujungnya pasti 400. */}
                      {ef?.kelompok_leasing && akunPiutangLeasing && (
                        <option value={akunPiutangLeasing.id}>
                          {akunPiutangLeasing.code} · {akunPiutangLeasing.name}
                        </option>
                      )}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-500 mt-2">
        💡 "Ikuti bawaan": Transfer/Debit/EDC ber-nama bank → rekening bank itu; metode lain → Kas Toko cabang penjualan.
        Pilihan eksplisit di kolom kanan selalu menang. Perubahan tercatat di Audit Trail.
        Mengganti NAMA metode tidak memutus pemetaannya (disimpan per id metode, bukan per nama).
      </p>
    </div>
  );
}

/* ============ Format Nomor Dokumen (UI untuk backend yang sudah lama siap) ============
 * Backend GET/PATCH /finance/pengaturan/nomor-dokumen(/{prefix}) (routers/finance/transactions.py)
 * memakai mesin nomor berurutan yang SAMA dengan Inventory PO/DO/TRM
 * (routers/inventory/_shared.py::nomor_urut_baru — race-safe via counter atomik),
 * tapi tidak pernah punya layar, jadi satu-satunya cara owner mengubahnya adalah
 * lewat database langsung. Prefix yang diatur di sini SEMUANYA sudah dipindah ke
 * mesin nomor berurutan ini (lihat FINANCE_SEQ_PREFIX di _shared.py) — TXN (baris
 * jurnal otomatis) SENGAJA TIDAK ikut, lihat catatan di kartu ini.
 *
 * Bentuk & istilah kartu ini SENGAJA diselaraskan dengan kartu "Format Nomor
 * Dokumen" di InventoryPengaturan.jsx (untuk PO/DO/TRM) — owner akan melihat dua
 * halaman ini bersebelahan, dua gaya berbeda untuk hal yang sama akan
 * membingungkan. `pratinjauNomorDokumen` di bawah sengaja DIDUPLIKASI (bukan
 * di-import) dari halaman itu, konsisten dengan pola `_next_code` yang juga
 * sengaja hidup dua tempat di backend — supaya dua halaman ini tidak saling
 * bergantung dan bisa dikerjakan/diubah independen. */
const NOMOR_PREFIX_LIST = ["UTG", "UTGB", "PIU", "RMB", "DIV", "OPN", "PTC-REP", "AST"];
const NOMOR_PREFIX_INFO = {
  UTG: { label: "Utang Usaha", ket: "Dipakai saat mencatat utang ke supplier/vendor atas pembelian barang/jasa yang belum dibayar." },
  UTGB: { label: "Utang Beban", ket: "Dipakai saat mencatat beban yang sudah terjadi tapi belum dibayar (mode \"Utang\" di Catat Cepat) — kas belum berkurang, baru jadi kewajiban di akun 2300." },
  PIU: { label: "Piutang", ket: "Dipakai saat mencatat tagihan ke pelanggan yang belum dibayar." },
  RMB: { label: "Reimbursement", ket: "Dipakai saat karyawan mengajukan klaim penggantian biaya yang sudah ditalangi lebih dulu." },
  DIV: { label: "Dividen", ket: "Dipakai saat mencatat deklarasi pembagian laba ke pemegang saham." },
  OPN: { label: "Opname Persediaan", ket: "Dipakai saat mencatat hasil hitung fisik stok dan selisihnya terhadap catatan sistem." },
  "PTC-REP": { label: "Pengisian Ulang Kas Kecil", ket: "Dipakai saat mengisi ulang saldo kas kecil (petty cash) kembali ke nilai tetapnya (imprest)." },
  AST: { label: "Aset Tetap", ket: "Dipakai saat mendaftarkan aset tetap perusahaan (kendaraan, peralatan, dll) yang akan disusutkan tiap bulan." },
};

const NOMOR_RESET_OPTIONS = [
  { v: "tahun", l: "Setiap tahun — nomor urut kembali ke 1 tiap tanggal 1 Januari" },
  { v: "bulan", l: "Setiap bulan — nomor urut kembali ke 1 tiap awal bulan" },
  { v: "tidak", l: "Tidak pernah — nomor urut terus naik, tidak pernah kembali ke 1" },
];

const NOMOR_TOKEN_INFO = [
  { t: "{prefix}", ket: "Kode dokumennya sendiri (mis. UTG, PIU, DIV)" },
  { t: "{tahun}", ket: "Tahun saat dokumen dibuat, 4 angka (contoh: 2026)" },
  { t: "{bulan}", ket: "Bulan saat dokumen dibuat, selalu 2 angka (contoh: 08 untuk Agustus)" },
  { t: "{urut}", ket: "Nomor urut otomatis yang naik sendiri tiap dokumen baru dibuat" },
];

/** Pratinjau format nomor dokumen di sisi klien — meniru `str.format()` yang
 * dipakai backend (`nomor_urut_baru` di routers/inventory/_shared.py, dipakai
 * ulang oleh Finance lewat `_next_seq_code`) supaya owner melihat hasilnya SAAT
 * MENGETIK, bukan menebak lalu kecewa setelah simpan. Token tidak dikenal
 * ditandai sebagai error, sama seperti backend yang menolak format tidak valid. */
function pratinjauNomorDokumen(format, prefix, digit) {
  const now = new Date();
  const tahun = String(now.getFullYear());
  const bulan = String(now.getMonth() + 1).padStart(2, "0");
  const digitAman = Math.max(1, Math.min(10, Number(digit) || 5));
  const urut = "1".padStart(digitAman, "0");
  const nilaiToken = { prefix, tahun, bulan, urut };
  let tokenTakDikenal = null;
  const hasil = String(format || "").replace(/\{([a-zA-Z]+)\}/g, (cocok, nama) => {
    if (!(nama in nilaiToken)) { tokenTakDikenal = nama; return cocok; }
    return nilaiToken[nama];
  });
  return { hasil, error: tokenTakDikenal ? `Token {${tokenTakDikenal}} tidak dikenal` : null };
}

const nomorInpCls = "h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200 disabled:bg-slate-50 disabled:text-slate-400";

function FormatNomorDokumenCard() {
  const { can } = useAuth();
  // Digerbangi izin modul "finance_pengaturan" yang sudah ada (yang sama dipakai
  // GET/PATCH di backend) — "view" untuk membuka kartu ini, "update" untuk
  // menyimpan perubahan. Tombol Simpan disembunyikan kalau update-nya tidak ada,
  // supaya orang yang cuma bisa lihat tidak menekan tombol yang ujungnya 403.
  const canView = can("finance_pengaturan", "view");
  const canUpdate = can("finance_pengaturan", "update");

  const [nomor, setNomor] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [busyPrefix, setBusyPrefix] = useState("");

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await api.get("/finance/pengaturan/nomor-dokumen");
      setNomor(r.data.items || {});
    } catch (e) { setLoadError(e); toast.error(e?.response?.data?.detail || "Gagal memuat format nomor dokumen"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (canView) load(); else setLoading(false); }, [canView]); // eslint-disable-line react-hooks/exhaustive-deps

  const ubah = (prefix, patch) => setNomor((s) => ({ ...s, [prefix]: { ...s[prefix], ...patch } }));

  const simpan = async (prefix) => {
    const cfg = nomor[prefix] || {};
    const digit = Number(cfg.digit) || 5;
    if (digit < 1 || digit > 10) { toast.error("Jumlah digit nomor urut harus 1-10"); return; }
    setBusyPrefix(prefix);
    try {
      const r = await api.patch(`/finance/pengaturan/nomor-dokumen/${prefix}`, {
        format: cfg.format, reset: cfg.reset, digit,
      });
      toast.success(r.data.message);
      setNomor((s) => ({ ...s, [prefix]: r.data.config }));
    } catch (e) { toast.error(e?.response?.data?.detail || `Gagal menyimpan format nomor ${prefix}`); }
    finally { setBusyPrefix(""); }
  };

  // Tidak punya izin lihat sama sekali → kartu ini tidak ditampilkan (bukan pesan
  // "akses ditolak" — halaman Pengaturan tetap bisa dibuka untuk kartu lain yang
  // izinnya berbeda, mis. Approval Threshold & Pemetaan Pembayaran di atas).
  if (!canView) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="format-nomor-dokumen-card">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 grid place-items-center"><Hash className="w-5 h-5" /></div>
        <div>
          <p className="font-display font-bold text-base text-slate-900">Format Nomor Dokumen</p>
          <p className="text-xs text-slate-500">Atur pola penomoran otomatis untuk dokumen Finance — seperti Accurate Online.</p>
        </div>
      </div>

      <div className="mt-3 mb-3 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5">
        <p className="text-xs font-semibold text-slate-700 mb-1.5 flex items-center gap-1"><Info className="w-3.5 h-3.5" /> Kode yang bisa dipakai di format:</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
          {NOMOR_TOKEN_INFO.map((t) => (
            <p key={t.t} className="text-[11px] text-slate-600">
              <code className="px-1 py-0.5 rounded bg-white border border-slate-200 text-emerald-700 font-mono">{t.t}</code> — {t.ket}
            </p>
          ))}
        </div>
      </div>

      <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 text-[11px] text-amber-800 space-y-1.5">
        <p>
          <b>Dokumen LAMA tidak ikut berubah nomornya.</b> Kalau format diganti sekarang, dokumen yang sudah
          terlanjur bernomor format lama (contoh: <code className="font-mono">UTG-20260115093000-A1B2</code>) tetap
          seperti apa adanya — ini disengaja. Nomor utang/piutang/dividen dsb yang sudah disebutkan lisan ke
          supplier/karyawan/pemegang saham tidak boleh berubah di belakang layar, karena itu akan mengacaukan rekonsiliasi.
        </p>
        <p>
          <b>Baris jurnal otomatis (kode <code className="font-mono">TXN</code>) SENGAJA TIDAK ada di daftar ini.</b> Setiap
          catatan uang masuk/keluar/transfer di menu Catat Cepat memakai format timestamp seperti biasa dan
          bukan penomoran berurutan — volumenya jauh lebih besar dan nyaris tidak pernah dirujuk balik ke
          siapa pun, jadi jangan bingung mencarinya di sini.
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500" data-testid="format-nomor-dokumen-loading">Memuat...</p>
      ) : loadError ? (
        <div className="text-sm text-rose-600 flex items-center justify-between gap-2">
          <span>Gagal memuat format nomor dokumen.</span>
          <button onClick={load} className="text-xs underline">Coba lagi</button>
        </div>
      ) : (
        <div className="space-y-5">
          {NOMOR_PREFIX_LIST.map((prefix) => {
            const cfg = nomor[prefix] || { format: "{prefix}-{tahun}-{urut}", reset: "tahun", digit: 5 };
            const { hasil, error } = pratinjauNomorDokumen(cfg.format, prefix, cfg.digit);
            return (
              <div key={prefix} className="border border-slate-200 rounded-lg p-4" data-testid={`nomor-dok-row-${prefix}`}>
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {NOMOR_PREFIX_INFO[prefix].label} <span className="text-slate-400 font-mono text-xs">({prefix})</span>
                    </p>
                    <p className="text-[11px] text-slate-500">{NOMOR_PREFIX_INFO[prefix].ket}</p>
                  </div>
                  {canUpdate && (
                    <button onClick={() => simpan(prefix)} disabled={busyPrefix === prefix || !!error}
                            data-testid={`nomor-dok-simpan-${prefix}`}
                            className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
                      <Save className="w-3.5 h-3.5" /> {busyPrefix === prefix ? "Menyimpan..." : "Simpan"}
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1" htmlFor={`fmt-${prefix}`}>
                      Format
                    </label>
                    <input id={`fmt-${prefix}`} type="text" value={cfg.format || ""} disabled={!canUpdate}
                           onChange={(e) => ubah(prefix, { format: e.target.value })}
                           data-testid={`nomor-dok-format-${prefix}`}
                           placeholder="{prefix}-{tahun}-{urut}"
                           className={`${nomorInpCls} w-full font-mono`} />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1" htmlFor={`digit-${prefix}`}>
                      Jumlah digit urut
                    </label>
                    <input id={`digit-${prefix}`} type="number" min={1} max={10} value={cfg.digit ?? 5} disabled={!canUpdate}
                           onChange={(e) => ubah(prefix, { digit: e.target.value })}
                           data-testid={`nomor-dok-digit-${prefix}`}
                           className={`${nomorInpCls} w-28 text-center`} />
                  </div>
                </div>

                <div className="mt-3">
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Kapan nomor urut kembali ke 1?</label>
                  <div className="flex flex-col gap-1.5">
                    {NOMOR_RESET_OPTIONS.map((opt) => (
                      <label key={opt.v} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer select-none">
                        <input type="radio" name={`reset-${prefix}`} checked={(cfg.reset || "tahun") === opt.v}
                               disabled={!canUpdate} onChange={() => ubah(prefix, { reset: opt.v })}
                               data-testid={`nomor-dok-reset-${prefix}-${opt.v}`}
                               className="w-3.5 h-3.5 accent-emerald-600" />
                        {opt.l}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Pratinjau langsung — inti permintaan owner: lihat hasilnya SAAT mengetik. */}
                <div className={`mt-3 rounded-lg px-3 py-2.5 flex items-center gap-2 ${error ? "bg-rose-50 border border-rose-200" : "bg-emerald-50 border border-emerald-200"}`}>
                  {error ? <XCircle className="w-4 h-4 text-rose-600 shrink-0" /> : <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />}
                  <div>
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Pratinjau nomor dokumen berikutnya</p>
                    {error ? (
                      <p className="text-sm font-mono font-bold text-rose-700" data-testid={`nomor-dok-preview-${prefix}`}>{error}</p>
                    ) : (
                      <p className="text-sm font-mono font-bold text-emerald-800" data-testid={`nomor-dok-preview-${prefix}`}>{hasil}</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!canUpdate && !loading && !loadError && (
        <p className="text-[11px] text-slate-500 mt-3" data-testid="format-nomor-dokumen-readonly">
          Anda hanya bisa melihat format nomor dokumen ini. Hubungi Finance CEO untuk mengubahnya.
        </p>
      )}
    </div>
  );
}

/* ============ K3 (brief owner 4 Sep 2026) — Kode Nomor Bukti Kas Kecil ============
 * Nomor bukti kas kecil berbentuk [METODE]-[CABANG]-[YYYYMM]-[NNNN]
 * (mis. PCT-PST-202609-0001). BENTUKnya ditetapkan brief dan tidak bisa diubah —
 * yang bisa diatur owner hanya KODEnya. Karena itu kartu ini terpisah dari
 * "Format Nomor Dokumen" di atas: yang di sana mengubah POLA, yang di sini
 * mengisi KAMUS.
 *
 * Pratinjau contoh nomor diambil dari BACKEND, bukan dirangkai di sini: angka
 * urutnya hidup di counter server, dan pratinjau yang dikarang klien selalu
 * menunjukkan 0001 apa pun keadaan sebenarnya — bug yang sudah pernah
 * diperbaiki sekali (brief owner 17 Agu #11). */
const METODE_INFO = [
  { k: "PCT", l: "Keluar · Tunai", ket: "Pengeluaran dari kas tunai" },
  { k: "PCE", l: "Keluar · E-Wallet", ket: "Pengeluaran dari e-wallet" },
  { k: "PIT", l: "Masuk · Tunai", ket: "Top-up kas tunai (saat disetujui)" },
  { k: "PIE", l: "Masuk · E-Wallet", ket: "Top-up e-wallet (saat disetujui)" },
];

function KodeNomorBuktiKasKecilCard() {
  const { can } = useAuth();
  // Kunci yang SAMA dengan backend: GET boleh finance_petty.view ATAU
  // finance_pengaturan.view (`_boleh_baca_kode_bukti`), PUT hanya
  // finance_pengaturan.update. Tombol Simpan disembunyikan kalau update tidak
  // ada, supaya tidak ada tombol yang ujungnya 403 (CLAUDE.md #3).
  const canView = can("finance_pengaturan", "view") || can("finance_petty", "view");
  const canUpdate = can("finance_pengaturan", "update");

  const [reg, setReg] = useState(null);
  const [draf, setDraf] = useState({ metode: {}, pusat_cabang: "", cabang: {} });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [contoh, setContoh] = useState(null);

  const pasang = (d) => {
    setReg(d);
    setDraf({
      metode: { ...d.metode },
      pusat_cabang: d.pusat_cabang || "",
      cabang: Object.fromEntries((d.cabang || []).map((b) => [b.cabang, b.kode || ""])),
    });
  };

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await api.get("/finance/petty-cash/kode-bukti");
      pasang(r.data);
    } catch (e) { setLoadError(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (canView) load(); else setLoading(false); }, [canView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Contoh nomor SUNGGUHAN: satu akun kas kecil yang sudah siap dipakai sebagai
  // sampel. Kalau belum ada satu pun akun siap, pratinjau memang tidak bisa
  // ditampilkan — dan itu dikatakan apa adanya, bukan diganti angka karangan.
  useEffect(() => {
    if (!canView || !reg) return;
    let batal = false;
    api.get("/finance/petty-cash/accounts")
      .then((r) => {
        const siap = (r.data.items || []).find((a) => a.kode_bukti_siap);
        if (!siap) { if (!batal) setContoh({ pesan: "Belum ada akun kas kecil yang siap — setel Jenis Kas & kode cabangnya dulu." }); return null; }
        return api.get("/finance/petty-cash/nomor-bukti/pratinjau", { params: { akun_id: siap.id, arah: "keluar" } })
          .then((p) => { if (!batal) setContoh({ nomor: p.data.nomor, akun: siap.name }); });
      })
      .catch(() => { if (!batal) setContoh({ pesan: "Pratinjau tidak bisa dimuat." }); });
    return () => { batal = true; };
  }, [canView, reg]);

  const simpan = async () => {
    // HANYA yang benar-benar diubah yang dikirim. Backend meng-upsert baris
    // cabang satu per satu (pipeline `$filter`+`$concatArrays`), jadi mengirim
    // seluruh tabel tidak merusak apa pun — tapi ia akan mengubah SETIAP cabang
    // jadi entri "config", termasuk yang isinya masih persis kode bawaan. Sejak
    // itu bawaan tidak lagi berlaku untuk cabang tersebut, dan kode yang
    // dikosongkan orang lain tidak akan pernah kembali ke bawaannya.
    const semula = Object.fromEntries((reg.cabang || []).map((b) => [b.cabang, b.kode || ""]));
    const cabang = Object.entries(draf.cabang)
      .filter(([nama, kode]) => (kode || "") !== (semula[nama] || ""))
      .map(([nama, kode]) => ({ cabang: nama, kode: kode || "" }));
    const metode = Object.fromEntries(
      Object.entries(draf.metode).filter(([k, v]) => (v || "") !== (reg.metode[k] || "")));
    const pusatBerubah = draf.pusat_cabang !== reg.pusat_cabang;
    if (!cabang.length && !Object.keys(metode).length && !pusatBerubah) {
      toast.info("Tidak ada perubahan untuk disimpan");
      return;
    }
    setBusy(true);
    try {
      const body = {};
      if (cabang.length) body.cabang = cabang;
      if (Object.keys(metode).length) body.metode = metode;
      if (pusatBerubah) body.pusat_cabang = draf.pusat_cabang;
      const r = await api.put("/finance/petty-cash/kode-bukti", body);
      toast.success(r.data.message);
      pasang(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan kode nomor bukti"); }
    finally { setBusy(false); }
  };

  if (!canView) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="petty-kode-bukti-card">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-lg bg-purple-50 text-purple-600 grid place-items-center"><Hash className="w-5 h-5" /></div>
        <div>
          <p className="font-display font-bold text-base text-slate-900">Kode Nomor Bukti Kas Kecil</p>
          <p className="text-xs text-slate-500">Kamus kode untuk nomor <code className="font-mono">[METODE]-[CABANG]-[YYYYMM]-[NNNN]</code>.</p>
        </div>
      </div>

      <div className="mt-3 mb-4 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 text-[11px] text-amber-800 space-y-1.5">
        <p>
          <b>Cabang tanpa kode TIDAK bisa mencatat pengeluaran kas kecil.</b> Itu disengaja (keputusan owner
          5 Sep 2026): nomor bukti tidak pernah ditebak. Kosongkan kode hanya kalau cabang itu memang tidak
          memakai kas kecil.
        </p>
        <p>
          <b>Bentuk nomornya tidak bisa diubah dari sini</b> — hanya kodenya. Nomor yang sudah terlanjur terbit
          tidak ikut berubah saat kode diganti; deret barunya mulai dari kode yang baru.
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500" data-testid="petty-kode-loading">Memuat...</p>
      ) : loadError || !reg ? (
        <div className="text-sm text-rose-600 flex items-center justify-between gap-2">
          <span>Gagal memuat kode nomor bukti kas kecil.</span>
          <button onClick={load} className="text-xs underline">Coba lagi</button>
        </div>
      ) : (
        <div className="space-y-5">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Kode metode</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {METODE_INFO.map((m) => (
                <div key={m.k} className="flex items-center gap-3">
                  <input value={draf.metode[m.k] || ""} disabled={!canUpdate} maxLength={5}
                         onChange={(e) => setDraf((s) => ({ ...s, metode: { ...s.metode, [m.k]: e.target.value.toUpperCase() } }))}
                         data-testid={`petty-kode-metode-${m.k}`}
                         className={`${nomorInpCls} w-24 font-mono text-center`} />
                  <div>
                    <p className="text-xs font-semibold text-slate-800">{m.l}</p>
                    <p className="text-[11px] text-slate-500">{m.ket}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5" htmlFor="petty-kode-pusat-sel">
              Cabang yang berperan sebagai PUSAT
            </label>
            <select id="petty-kode-pusat-sel" value={draf.pusat_cabang} disabled={!canUpdate}
                    onChange={(e) => setDraf((s) => ({ ...s, pusat_cabang: e.target.value }))}
                    data-testid="petty-kode-pusat" className={`${nomorInpCls} w-full sm:w-80`}>
              {(reg.cabang || []).map((b) => <option key={b.cabang} value={b.cabang}>{b.cabang}</option>)}
            </select>
            <p className="text-[11px] text-slate-500 mt-1">
              Kas kecil yang cabangnya dikosongkan (mis. akun 1050 &amp; jalur Marketing) memakai kode cabang ini.
              Tidak boleh dikosongkan — semua kas tanpa cabang akan kehilangan nomornya sekaligus.
            </p>
          </div>

          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Kode per cabang</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <tbody>
                  {(reg.cabang || []).map((b) => (
                    <tr key={b.cabang} className="border-b border-slate-100 last:border-0">
                      <td className="py-2 pr-3 text-slate-700">{b.cabang}</td>
                      <td className="py-2 pr-3 w-32">
                        <input value={draf.cabang[b.cabang] ?? ""} disabled={!canUpdate} maxLength={5}
                               onChange={(e) => setDraf((s) => ({ ...s, cabang: { ...s.cabang, [b.cabang]: e.target.value.toUpperCase() } }))}
                               placeholder="—"
                               data-testid={`petty-kode-cabang-${b.cabang}`}
                               className={`${nomorInpCls} w-full font-mono text-center`} />
                      </td>
                      <td className="py-2 text-[11px] whitespace-nowrap">
                        {!(draf.cabang[b.cabang] || "") ? (
                          <span className="px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">belum ada kode</span>
                        ) : b.sumber === "config" ? (
                          <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">config</span>
                        ) : (
                          <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">bawaan</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {(reg.kode_yatim || []).length > 0 && (
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5" data-testid="petty-kode-yatim">
              <p className="text-[11px] font-semibold text-slate-700 mb-1 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Kode untuk cabang yang sudah tidak terdaftar</p>
              <p className="text-[11px] text-slate-600 mb-1.5">
                Tidak dipakai menerbitkan nomor, tapi tetap MEMAKAI kodenya (kode yang sama tidak bisa dipakai cabang lain).
                Kosongkan kodenya kalau cabang itu memang sudah tidak ada.
              </p>
              <div className="flex flex-wrap gap-2">
                {reg.kode_yatim.map((y) => (
                  <span key={y.cabang} className="text-[11px] font-mono px-2 py-1 rounded bg-white border border-slate-200">{y.cabang} → {y.kode || "—"}</span>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-lg px-3 py-2.5 border border-purple-200 bg-purple-50" data-testid="petty-kode-pratinjau">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Contoh nomor berikutnya (dari server)</p>
            {contoh && contoh.nomor ? (
              <p className="text-sm font-mono font-bold text-purple-800">{contoh.nomor} <span className="font-sans font-normal text-[11px] text-slate-500">· {contoh.akun}</span></p>
            ) : (
              <p className="text-[11px] text-slate-600">{(contoh && contoh.pesan) || "Menghitung..."}</p>
            )}
          </div>

          {canUpdate ? (
            <button onClick={simpan} disabled={busy} data-testid="petty-kode-simpan"
                    className="h-9 px-4 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
              <Save className="w-3.5 h-3.5" /> {busy ? "Menyimpan..." : "Simpan kode"}
            </button>
          ) : (
            <p className="text-[11px] text-slate-500" data-testid="petty-kode-readonly">
              Anda hanya bisa melihat kode ini. Hubungi pemegang izin Pengaturan Finance untuk mengubahnya.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
